import { useMemo } from 'react';
import { useLiveTrades } from '../hooks/useLiveTrades';
import { openMarketIds, withLiveProbabilities } from './model/live';
import type { EventModel } from './model/types';

const ALL_MARKETS: Set<string> = new Set();
// The rows show one decimal of a percent, so finer ticks need no re-render.
const PRECISION = 1000;

/** The event with the streamed prices of its open markets laid over the fetched ones. */
export function useLiveEvent(event: EventModel | null): EventModel | null {
    const streamMarketIds = useMemo(() => openMarketIds(event), [event]);
    const { liveProbs } = useLiveTrades({
        streamMarketIds,
        effectiveSelectedIds: ALL_MARKETS,
    });

    const rounded = useMemo(() => {
        const map = new Map<string, number>();
        for (const [id, p] of liveProbs) {
            map.set(id, Math.round(p * PRECISION) / PRECISION);
        }
        return map;
    }, [liveProbs]);
    const key = [...rounded].map(([id, p]) => `${id}=${p}`).join('|');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stable = useMemo(() => rounded, [key]);

    return useMemo(
        () => (event ? withLiveProbabilities(event, stable) : null),
        [event, stable]
    );
}
